// src/views/AccommodationProvider/ProviderPoliciesInsurances.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderPoliciesInsurances = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  
  // Get provider information from localStorage
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // State management
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [validationErrors, setValidationErrors] = useState({});
  
  // Policies checklist state
  const [policiesChecklist, setPoliciesChecklist] = useState({
    // Policies & Rules
    house_rules: false,
    paia_compliant: false,
    popia_compliant: false,
    security_communication: false,
    
    // Student Contracting
    standard_lease_agreements: false,
    no_illegal_deposits: false
  });

  // Insurance documents state
  const [existingDocuments, setExistingDocuments] = useState({
    property_insurance: null,
    property_insurance_status: 'not_provided',
    property_insurance_expiry_date: null,
    property_insurance_comments: null,
    
    liability_insurance: null,
    liability_insurance_status: 'not_provided', 
    liability_insurance_expiry_date: null,
    liability_insurance_comments: null,
    
    workers_compensation: null,
    workers_compensation_status: 'not_provided',
    workers_compensation_expiry_date: null,
    workers_compensation_comments: null,
    
    other_insurances: null,
    other_insurances_status: 'not_provided',
    other_insurances_expiry_date: null,
    other_insurances_comments: null
  });

  // Fetch property and existing data on component mount
  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!propertyId) {
        setAlert({
          type: 'danger',
          message: 'Property ID is required',
          visible: true
        });
        setLoading(false);
        return;
      }

      try {
        // Fetch property details
        const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
        setProperty(propertyResponse.data);

        // Fetch existing policies and insurance data
        const policiesResponse = await axiosInstance.get(`/api/properties/${propertyId}/policies-insurances/`);
        if (policiesResponse.data.checklist) {
          setPoliciesChecklist({
            house_rules: policiesResponse.data.checklist.house_rules || false,
            paia_compliant: policiesResponse.data.checklist.paia_compliant || false,
            popia_compliant: policiesResponse.data.checklist.popia_compliant || false,
            security_communication: policiesResponse.data.checklist.security_communication || false,
            standard_lease_agreements: policiesResponse.data.checklist.standard_lease_agreements || false,
            no_illegal_deposits: policiesResponse.data.checklist.no_illegal_deposits || false
          });
        }
        if (policiesResponse.data.documents) {
          setExistingDocuments(policiesResponse.data.documents);
        }

      } catch (error) {
        console.error('Error fetching property data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load property information. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, [propertyId]);

  // Handle checkbox changes
  const handleCheckboxChange = (field) => {
    setPoliciesChecklist(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    
    // Clear validation error for this field when checked
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  // Validate all checkboxes are checked
  const validateChecklist = () => {
    const errors = {};
    let hasErrors = false;

    // Check all fields in the checklist
    Object.keys(policiesChecklist).forEach(field => {
      if (!policiesChecklist[field]) {
        errors[field] = true;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  // Save policies checklist
  const handleSavePolicies = async () => {
    // Validate all checkboxes are checked
    if (!validateChecklist()) {
      setAlert({
        type: 'danger',
        message: 'Please ensure all policies and contracting standards are met before saving. All checkboxes must be checked.',
        visible: true
      });
      
      // Scroll to top to show the alert
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save policies checklist
      await axiosInstance.post(`/api/properties/${propertyId}/policies-insurances/`, policiesChecklist);
      
      setAlert({
        type: 'success',
        message: 'Policies checklist saved successfully! All policy and contracting standards have been verified.',
        visible: true
      });
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving policies:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to save policies checklist. Please try again.',
        visible: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    setIsSubmitting(true);
    try {
      // Upload document
      const formData = new FormData();
      formData.append(documentType, file);
      const response = await axiosInstance.post(`/api/properties/${propertyId}/policies-insurances/documents/`, formData);
      
      console.log('Upload response:', response.data);
      
      // Update the existing documents state with the new data
      if (response.data.data) {
        console.log('Updating documents state with:', response.data.data);
        setExistingDocuments(response.data.data);
      }
      
      // Also refresh the entire data to make sure we have the latest status
      try {
        const refreshResponse = await axiosInstance.get(`/api/properties/${propertyId}/policies-insurances/`);
        if (refreshResponse.data.documents) {
          console.log('Refreshed documents data:', refreshResponse.data.documents);
          setExistingDocuments(refreshResponse.data.documents);
        }
      } catch (refreshError) {
        console.warn('Failed to refresh data after upload:', refreshError);
      }
      
      setAlert({
        type: 'success',
        message: response.data.message || `${documentType.replace('_', ' ')} uploaded successfully and is pending approval.`,
        visible: true
      });
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error uploading document:', error);

      let errorMessage = 'Failed to upload document. Please try again.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setAlert({
        type: 'danger',
        message: errorMessage,
        visible: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle file input change
  const handleFileChange = async (documentType, event) => {
    const file = event.target.files[0];
    if (file) {
      await handleFileUpload(documentType, file);
    }
  };

  // Get status badge component
  const getStatusBadge = (status) => {
    const statusConfig = {
      'not_provided': { color: 'secondary', text: 'Not Provided' },
      'pending_approval': { color: 'warning', text: 'Pending Approval' },
      'rejected': { color: 'danger', text: 'Rejected' },
      'accepted': { color: 'success', text: 'Accepted' }
    };
    
    const config = statusConfig[status] || statusConfig['not_provided'];
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  // Handle back navigation
  const handleBackToOperationalStandards = () => {
    navigate(`/provider-operational-standards/${propertyId}`);
  };

  // Handle next navigation
  const handleNextToInspection = () => {
    navigate('/provider-request-inspection');
  };

  // Show loading spinner
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading policies & insurances...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <Row>
        <Col>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">Policies & Insurances - {property?.trading_name || 'Property'}</h2>
              {representativeName && (
                <p className="text-muted mb-1">Representative: {representativeName}</p>
              )}
              <span className="me-2 badge bg-info">Business/Organization</span>
              <span className="badge bg-warning">Policies & Insurance</span>
            </div>
            <Button size="sm" color="outline-secondary" onClick={handleBackToOperationalStandards}>
              <i className="bi bi-arrow-left me-2"></i>
              Back to Operational Standards
            </Button>
          </div>

          {/* Alert */}
          {alert.visible && (
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({...alert, visible: false})} 
              className="mb-4"
            >
              {alert.message}
            </Alert>
          )}

          {/* Information Alert */}
          <Alert color="info" className="mb-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Complete Policies & Insurances:</strong> Confirm all required policies are in place and upload current insurance documents for review.
          </Alert>

          {/* Document Status Information */}
          <Alert color="light" className="border mb-4">
            <h6 className="mb-2">
              <i className="bi bi-clipboard-check me-2"></i>
              Document Review Status
            </h6>
            <ul className="mb-0 small">
              <li><Badge color="warning">Pending Approval</Badge> - Your document is being reviewed by our team</li>
              <li><Badge color="success">Accepted</Badge> - Document approved. Expiry date shown if applicable</li>
              <li><Badge color="danger">Rejected</Badge> - Document needs correction. See rejection reason below</li>
            </ul>
          </Alert>

          {/* Full Width: Policies Checklist */}
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <CardTitle tag="h4" className="mb-4">
                    <i className="bi bi-check2-square me-2 text-primary"></i>
                    Policies & Compliance Checklist
                  </CardTitle>

                  {/* Policies & Rules Section */}
                  <div className="p-3 rounded mb-3" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #6f42c1'}}>
                    <h5 className="mb-3" style={{color: '#6f42c1'}}>
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Policies & Rules
                    </h5>
                    <div className="border rounded p-3 bg-white">
                      {[
                        { key: 'house_rules', label: 'House Rules' },
                        { key: 'paia_compliant', label: 'PAIA (Promotion of Access to Information Act) Compliant' },
                        { key: 'popia_compliant', label: 'POPIA (Protection of Personal Information Act) Compliant' },
                        { key: 'security_communication', label: 'Security & Communication Protocols' }
                      ].map((item) => (
                        <FormGroup check className="mb-3" key={item.key}>
                          <Input
                            type="checkbox"
                            id={item.key}
                            checked={policiesChecklist[item.key]}
                            onChange={() => handleCheckboxChange(item.key)}
                            disabled={regStatus !== '2' || isSubmitting}
                            className={validationErrors[item.key] ? 'border-danger' : ''}
                          />
                          <Label 
                            check 
                            for={item.key} 
                            className={`ms-2 fw-bold ${validationErrors[item.key] ? 'text-danger' : ''}`}
                          >
                            {item.label}
                          </Label>
                          {validationErrors[item.key] && (
                            <div className="text-danger small mt-1 ms-4">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This policy standard is required
                            </div>
                          )}
                        </FormGroup>
                      ))}
                    </div>
                  </div>

                  {/* Student Contracting Section */}
                  <div className="p-3 rounded mb-3" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #20c997'}}>
                    <h5 className="mb-3" style={{color: '#20c997'}}>
                      <i className="bi bi-file-earmark-check me-2"></i>
                      Student Contracting
                    </h5>
                    <div className="border rounded p-3 bg-white">
                      {[
                        { key: 'standard_lease_agreements', label: 'Standard lease agreements' },
                        { key: 'no_illegal_deposits', label: 'No illegal deposits and exploitations' }
                      ].map((item) => (
                        <FormGroup check className="mb-3" key={item.key}>
                          <Input
                            type="checkbox"
                            id={item.key}
                            checked={policiesChecklist[item.key]}
                            onChange={() => handleCheckboxChange(item.key)}
                            disabled={regStatus !== '2' || isSubmitting}
                            className={validationErrors[item.key] ? 'border-danger' : ''}
                          />
                          <Label 
                            check 
                            for={item.key} 
                            className={`ms-2 fw-bold ${validationErrors[item.key] ? 'text-danger' : ''}`}
                          >
                            {item.label}
                          </Label>
                          {validationErrors[item.key] && (
                            <div className="text-danger small mt-1 ms-4">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This contracting standard is required
                            </div>
                          )}
                        </FormGroup>
                      ))}
                    </div>
                  </div>

                  {/* Save Policies Button */}
                  <div className="text-center mt-4">
                    <Button 
                      color="primary" 
                      size="lg" 
                      className="px-4"
                      onClick={handleSavePolicies}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Save Policies Checklist
                        </>
                      )}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Insurance Document Uploads Section - Matching Operational Standards Style */}
          <Row className="mt-5">
            <Col>
              <h3 className="text-primary mb-4">
                <i className="bi bi-cloud-upload me-2"></i>
                Upload Insurance Documents
              </h3>
              
              <Row className="mb-4">
                {/* Property Insurance */}
                <Col md="6">
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4" className="mb-4">
                        <i className="bi bi-building me-2 text-success"></i>
                        Property Insurance
                      </CardTitle>

                      {/* Document Status */}
                      <Row className="mb-4">
                        <Col md="8" className="mx-auto">
                          <div className="p-3 rounded" style={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6'}}>
                            <h6 className="mb-2">
                              <i className="bi bi-file-text me-2"></i>
                              Document Status
                            </h6>
                            {getStatusBadge(existingDocuments.property_insurance_status)}
                              
                              {/* Display expiry date if approved */}
                              {existingDocuments.property_insurance_status === 'accepted' && existingDocuments.property_insurance_expiry_date && (
                                <div className="mt-2">
                                  <small className="text-success">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    Valid until: {new Date(existingDocuments.property_insurance_expiry_date).toLocaleDateString()}
                                  </small>
                                </div>
                              )}
                              
                              {/* Display rejection comments if rejected */}
                              {existingDocuments.property_insurance_status === 'rejected' && existingDocuments.property_insurance_comments && (
                                <div className="mt-2">
                                  <small className="text-danger">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    <strong>Rejection reason:</strong> {existingDocuments.property_insurance_comments}
                                  </small>
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>

                      {/* File Input */}
                      <FormGroup className="mb-3">
                        <Label for="propertyInsurance">
                          <strong>Upload Property Insurance</strong>
                        </Label>
                        <Input
                          type="file"
                          id="propertyInsurance"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange('property_insurance', e)}
                          disabled={regStatus !== '2' || isSubmitting}
                        />
                      </FormGroup>
                      
                      <div className="text-center">
                        <small className="text-muted">
                          Documents will upload automatically when you select them.
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Liability Insurance */}
                <Col md="6">
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4" className="mb-4">
                        <i className="bi bi-shield-check me-2 text-warning"></i>
                        Liability Insurance
                      </CardTitle>

                      {/* Document Status */}
                      <Row className="mb-4">
                        <Col md="8" className="mx-auto">
                          <div className="p-3 rounded" style={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6'}}>
                            <h6 className="mb-2">
                              <i className="bi bi-file-text me-2"></i>
                              Document Status
                            </h6>
                            {getStatusBadge(existingDocuments.liability_insurance_status)}
                              
                              {/* Display expiry date if approved */}
                              {existingDocuments.liability_insurance_status === 'accepted' && existingDocuments.liability_insurance_expiry_date && (
                                <div className="mt-2">
                                  <small className="text-success">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    Valid until: {new Date(existingDocuments.liability_insurance_expiry_date).toLocaleDateString()}
                                  </small>
                                </div>
                              )}
                              
                              {/* Display rejection comments if rejected */}
                              {existingDocuments.liability_insurance_status === 'rejected' && existingDocuments.liability_insurance_comments && (
                                <div className="mt-2">
                                  <small className="text-danger">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    <strong>Rejection reason:</strong> {existingDocuments.liability_insurance_comments}
                                  </small>
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>

                      {/* File Input */}
                      <FormGroup className="mb-3">
                        <Label for="liabilityInsurance">
                          <strong>Upload Liability Insurance</strong>
                        </Label>
                        <Input
                          type="file"
                          id="liabilityInsurance"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange('liability_insurance', e)}
                          disabled={regStatus !== '2' || isSubmitting}
                        />
                      </FormGroup>
                      
                      <div className="text-center">
                        <small className="text-muted">
                          Documents will upload automatically when you select them.
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                {/* Workers Compensation */}
                <Col md="6">
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4" className="mb-4">
                        <i className="bi bi-people me-2 text-info"></i>
                        Workers Compensation
                      </CardTitle>

                      {/* Document Status */}
                      <Row className="mb-4">
                        <Col md="8" className="mx-auto">
                          <div className="p-3 rounded" style={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6'}}>
                            <h6 className="mb-2">
                              <i className="bi bi-file-text me-2"></i>
                              Document Status
                            </h6>
                            {getStatusBadge(existingDocuments.workers_compensation_status)}
                              
                              {/* Display expiry date if approved */}
                              {existingDocuments.workers_compensation_status === 'accepted' && existingDocuments.workers_compensation_expiry_date && (
                                <div className="mt-2">
                                  <small className="text-success">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    Valid until: {new Date(existingDocuments.workers_compensation_expiry_date).toLocaleDateString()}
                                  </small>
                                </div>
                              )}
                              
                              {/* Display rejection comments if rejected */}
                              {existingDocuments.workers_compensation_status === 'rejected' && existingDocuments.workers_compensation_comments && (
                                <div className="mt-2">
                                  <small className="text-danger">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    <strong>Rejection reason:</strong> {existingDocuments.workers_compensation_comments}
                                  </small>
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>

                      {/* File Input */}
                      <FormGroup className="mb-3">
                        <Label for="workersCompensation">
                          <strong>Upload Workers Compensation</strong>
                        </Label>
                        <Input
                          type="file"
                          id="workersCompensation"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange('workers_compensation', e)}
                          disabled={regStatus !== '2' || isSubmitting}
                        />
                      </FormGroup>
                      
                      <div className="text-center">
                        <small className="text-muted">
                          Documents will upload automatically when you select them.
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Other Insurances */}
                <Col md="6">
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4" className="mb-4">
                        <i className="bi bi-plus-circle me-2 text-secondary"></i>
                        Other Insurances
                      </CardTitle>

                      {/* Document Status */}
                      <Row className="mb-4">
                        <Col md="8" className="mx-auto">
                          <div className="p-3 rounded" style={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6'}}>
                            <h6 className="mb-2">
                              <i className="bi bi-file-text me-2"></i>
                              Document Status
                            </h6>
                            {getStatusBadge(existingDocuments.other_insurances_status)}
                              
                              {/* Display expiry date if approved */}
                              {existingDocuments.other_insurances_status === 'accepted' && existingDocuments.other_insurances_expiry_date && (
                                <div className="mt-2">
                                  <small className="text-success">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    Valid until: {new Date(existingDocuments.other_insurances_expiry_date).toLocaleDateString()}
                                  </small>
                                </div>
                              )}
                              
                              {/* Display rejection comments if rejected */}
                              {existingDocuments.other_insurances_status === 'rejected' && existingDocuments.other_insurances_comments && (
                                <div className="mt-2">
                                  <small className="text-danger">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    <strong>Rejection reason:</strong> {existingDocuments.other_insurances_comments}
                                  </small>
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>

                      {/* File Input */}
                      <FormGroup className="mb-3">
                        <Label for="otherInsurances">
                          <strong>Upload Other Insurances</strong>
                        </Label>
                        <Input
                          type="file"
                          id="otherInsurances"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange('other_insurances', e)}
                          disabled={regStatus !== '2' || isSubmitting}
                        />
                      </FormGroup>
                      
                      <div className="text-center">
                        <small className="text-muted">
                          Documents will upload automatically when you select them.
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Action Buttons */}
  
        </Col>
      </Row>
    </div>
  );
};

export default ProviderPoliciesInsurances;